import {
  Maybe,
  Program,
  ProgramCustomLabel,
  ProgramSequence,
} from "../components/generated/graphql";

// For a deeper explanation on the regex read the comment in app/lib/utils/label_utils.rb
const TRANSFORM_REGEX =
  /{{{?[^{}]+}?}}|[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-a-zA-Z0-9()@:%_+.~#?&/=]*|(((a|an) )*\b(mentors?\b(?! collective)|students?\b|mentorships?\b|schools?\b|mentees?\b))/gi;

type LocalProgramCustomLabel = Pick<
  ProgramCustomLabel,
  | "singularMentor"
  | "singularMentorWithIndeterminateArticle"
  | "pluralMentor"
  | "respectMentorUppercase"
  | "singularMentee"
  | "singularMenteeWithIndeterminateArticle"
  | "pluralMentee"
  | "respectMenteeUppercase"
  | "singularStudent"
  | "singularStudentWithIndeterminateArticle"
  | "pluralStudent"
  | "respectStudentUppercase"
  | "singularMentorship"
  | "singularMentorshipWithIndeterminateArticle"
  | "pluralMentorship"
  | "respectMentorshipUppercase"
  | "singularSchool"
  | "singularSchoolWithIndeterminateArticle"
  | "pluralSchool"
  | "respectSchoolUppercase"
>;
type LocalProgramSequence = Pick<ProgramSequence, "id" | "higherEdSegment">;
type LocalProgram = Pick<Program, "id"> & {
  programSequence: LocalProgramSequence;
  programCustomLabel?: Maybe<LocalProgramCustomLabel>;
};

// A spin off of this code lives in app/lib/utils/label_utils.rb. Any change in here needs to be checked
// in that file to ensure compatibility between BE and FE.
export class LabelTranslator {
  program?: Maybe<LocalProgram>;
  programCustomLabel?: Maybe<LocalProgramCustomLabel>;

  constructor(program: Maybe<LocalProgram> | undefined) {
    this.program = program;
    this.programCustomLabel = program?.programCustomLabel;
  }

  transformText(text: string): string {
    if (!this.programCustomLabel) return text;

    return text.replace(TRANSFORM_REGEX, (match, group) => {
      if (!group) return match;
      const lowerMatch = match.toLowerCase();
      if (lowerMatch.includes("mentorship")) {
        return this.translate(match, "mentorship");
      } else if (lowerMatch.includes("school")) {
        return this.translate(match, "school");
      } else if (lowerMatch.includes("mentor")) {
        return this.translate(match, "mentor");
      } else if (lowerMatch.includes("student")) {
        return this.translate(match, "student");
      } else if (lowerMatch.includes("mentee")) {
        return this.translate(match, "mentee");
      }
      return match;
    });
  }

  translate(
    phrase: string,
    replacement: "mentor" | "mentee" | "school" | "mentorship" | "student"
  ): string {
    const prefix = /^.*s$/i.test(phrase) ? "plural" : "singular";
    const upCaseReplacement = `${replacement[0].toUpperCase()}${replacement.slice(1)}`;
    const respectUppercaseKey =
      `respect${upCaseReplacement}Uppercase` as keyof LocalProgramCustomLabel;
    const labelKey = `${prefix}${upCaseReplacement}` as keyof LocalProgramCustomLabel;
    const respectUppercase = this.programCustomLabel?.[respectUppercaseKey] as boolean;
    let newWord = this.programCustomLabel?.[labelKey] as string;

    if (!respectUppercase) newWord = newWord?.toLowerCase();

    if (/^[Aa]n?\b/.test(phrase)) {
      newWord = this.#findReplacementWithIndeterminateArticle(
        phrase,
        upCaseReplacement,
        respectUppercase
      );
    }
    if (!newWord) return phrase;

    return this.#matchUppercase(phrase, newWord, respectUppercase);
  }

  #findReplacementWithIndeterminateArticle(
    phrase: string,
    replacement: string,
    respectUppercase: boolean
  ): string {
    const articleLabel =
      `singular${replacement}WithIndeterminateArticle` as keyof LocalProgramCustomLabel;
    let newWord = this.programCustomLabel?.[articleLabel] as string;
    if (!respectUppercase) newWord = newWord?.toLowerCase();
    if (phrase.startsWith("A")) {
      newWord = newWord.charAt(0).toUpperCase() + newWord.slice(1);
    } else {
      newWord = newWord.charAt(0).toLowerCase() + newWord.slice(1);
    }
    return newWord;
  }

  #matchUppercase(originalPhrase: string, newPhrase: string, respectUppercase: boolean): string {
    if (originalPhrase === originalPhrase.toUpperCase()) {
      return newPhrase.toUpperCase();
    } else if (respectUppercase) {
      return newPhrase;
    } else if (originalPhrase.split(" ").every((word) => /[A-Z]/.test(word.charAt(0)))) {
      return newPhrase.replace(/\b\w/g, (l) => l.toUpperCase());
    } else if (/[A-Z]/.test(originalPhrase.charAt(0))) {
      return newPhrase.charAt(0).toUpperCase() + newPhrase.slice(1);
    } else if (originalPhrase.split(" ").some((word) => /[A-Z]/.test(word.charAt(0)))) {
      const allUpper = newPhrase.replace(/\b\w/g, (l) => l.toUpperCase());
      return allUpper.charAt(0).toLowerCase() + allUpper.slice(1);
    }
    return newPhrase;
  }
}
